"use client"

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from "react"

// ─── Types (mirrored from backend) ───────────────────────────────────────────

export type Scope = "all" | "own" | "team" | "none"
export type Effect = "allow" | "deny"

export interface ResolvedPermission {
  resource: string
  action: string
  scope: Scope
  effect: Effect
  source: "own" | "inherited"
}

export interface UserPermissionsResponse {
  roleId: string
  roleName: string
  permissions: ResolvedPermission[]
}

// ─── Context type ─────────────────────────────────────────────────────────────

export interface PermissionsContextType {
  permissions: ResolvedPermission[]
  can(resource: string, action: string): boolean
  scope(resource: string, action: string): Scope
  isLoading: boolean
  error: string | null
  refresh(): Promise<void>
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined)

// ─── Helper: build full permissions for local dev ─────────────────────────────

function buildFullPermissions(): ResolvedPermission[] {
  const resources = [
    'users', 'templates', 'leads', 'appointments', 'patients', 'physiotherapists',
    'conversations', 'automations', 'reports', 'settings', 'audit_log', 'calendar',
    'ai_assistant', 'debug', 'logs', 'tenants', 'roles', 'properties', 'visits',
    'quotes', 'pipeline', 'widget', 'search', 'notifications', 'medical_appointments',
  ]
  const actions = [
    'read', 'view', 'create', 'update', 'delete', 'assign', 'export', 'use',
    'assign_role', 'reset_password', 'reply', 'send', 'manage', 'closeDeal',
    'moveLeads', 'forceClose', 'manageUsers', 'manageRoles', 'monitor',
    'manage_schedules',
  ]
  return resources.flatMap(resource =>
    actions.map(action => ({
      resource,
      action,
      scope: 'all' as Scope,
      effect: 'allow' as Effect,
      source: 'own' as const,
    }))
  )
}

// ─── Provider ─────────────────────────────────────────────────────────────────

interface PermissionsProviderProps {
  children: ReactNode
  /** When token or tenantId change, permissions are reloaded */
  token?: string | null
  tenantId?: string | null
}

export function PermissionsProvider({
  children,
  token,
  tenantId,
}: PermissionsProviderProps) {
  const [permissions, setPermissions] = useState<ResolvedPermission[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const fetchPermissions = useCallback(async () => {
    // Only fetch when we have auth context
    if (!token || !tenantId) {
      setPermissions([])
      setIsLoading(false)
      return
    }

    // Cancel any in-flight request
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setIsLoading(true)
    try {
      const res = await fetch("/api/permissions/me", {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-ID": tenantId,
        },
        signal: controller.signal,
      })

      if (!controller.signal.aborted && res.ok) {
        const json = await res.json()
        // Backend wraps response in { success, data: { roleId, roleName, permissions } }
        const payload = json?.data ?? json
        const perms = payload?.permissions ?? []
        setPermissions(perms.length > 0 ? perms : [])
        setError(perms.length > 0 ? null : "No permissions returned from server")
      } else if (!controller.signal.aborted) {
        // Fail-closed: deny all access on fetch failure
        setPermissions([])
        setError(`Failed to load permissions (HTTP ${res.status})`)
      }
    } catch (err) {
      // Ignore abort errors; fail-closed on network failure
      if (err instanceof DOMException && err.name === "AbortError") return
      setPermissions([])
      setError("Network error loading permissions")
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false)
      }
    }
  }, [token, tenantId])

  // Reload when token or tenant changes (login / tenant switch)
  useEffect(() => {
    fetchPermissions()
    return () => {
      abortRef.current?.abort()
    }
  }, [fetchPermissions])

  const can = useCallback(
    (resource: string, action: string): boolean => {
      // If no permissions loaded yet, deny (will re-evaluate after load)
      if (permissions.length === 0) return false

      const rule = permissions.find(
        (p) => p.resource === resource && p.action === action
      )
      if (rule) {
        if (rule.effect === "deny") return false
        if (rule.scope === "none") return false
        return true
      }

      // Fallback: if the user has ANY allow permission for this resource, grant access
      // This handles cases where the backend returns permissions with slightly different action names
      const anyResourceRule = permissions.find(
        (p) => p.resource === resource && p.effect === "allow" && p.scope !== "none"
      )
      if (anyResourceRule) return true

      return false
    },
    [permissions]
  )

  const scope = useCallback(
    (resource: string, action: string): Scope => {
      const rule = permissions.find(
        (p) => p.resource === resource && p.action === action
      )
      if (!rule || rule.effect === "deny") return "none"
      return rule.scope
    },
    [permissions]
  )

  const refresh = useCallback(async () => {
    await fetchPermissions()
  }, [fetchPermissions])

  const value: PermissionsContextType = {
    permissions,
    can,
    scope,
    isLoading,
    error,
    refresh,
  }

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  )
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePermissionsContext() {
  const ctx = useContext(PermissionsContext)
  if (ctx === undefined) {
    throw new Error("usePermissionsContext must be used within a PermissionsProvider")
  }
  return ctx
}
